import { useState } from "react";
import { ChevronDown, Trash2, StickyNote } from "lucide-react";
import { STATUSES } from "./AdminEnquiries";

const STATUS_STYLE = {
  new: "border-gold/60 text-gold",
  contacted: "border-champagne/60 text-champagne",
  booked: "border-emerald-400/60 text-emerald-300",
  declined: "border-stone-600 text-stone-500",
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

export default function EnquiryCard({ enquiry: e, onStatus, onDelete, onNotes }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [notes, setNotes] = useState(e.notes || "");
  const notesDirty = notes !== (e.notes || "");

  return (
    <article data-testid={`enquiry-card-${e.id}`} className="border border-white/10 bg-elevated hover:border-white/20 transition-colors">
      <button
        data-testid={`enquiry-toggle-${e.id}`}
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-6 py-5 grid grid-cols-[1fr_auto] sm:grid-cols-[2fr_1.5fr_1.5fr_auto_auto] gap-4 items-center"
      >
        <div>
          <p className="font-serif text-lg text-ivory leading-tight">{e.full_names}</p>
          <p className="text-xs font-light text-stone-400 truncate">{e.email}</p>
        </div>
        <p className="hidden sm:block text-xs font-light text-stone-300">
          <span className="block text-[9px] font-mono tracking-[0.2em] uppercase text-stone-500">Wedding</span>
          {fmtDate(e.event_date)} · {e.location}
        </p>
        <p className="hidden sm:block text-xs font-light text-stone-300 truncate">
          <span className="block text-[9px] font-mono tracking-[0.2em] uppercase text-stone-500">Collection</span>
          {e.package_interest}
        </p>
        <span data-testid={`enquiry-status-${e.id}`} className={`border px-3 py-1 text-[9px] font-mono tracking-[0.2em] uppercase ${STATUS_STYLE[e.status]}`}>
          {e.status}
        </span>
        <ChevronDown size={16} className={`text-stone-500 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div data-testid={`enquiry-details-${e.id}`} className="px-6 pb-6 border-t border-white/10 pt-5 grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <p className="text-[9px] font-mono tracking-[0.2em] uppercase text-stone-500">Vision & Story</p>
            <p className="mt-2 text-sm font-light leading-relaxed text-stone-200 whitespace-pre-line">{e.vision_story}</p>
            <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 text-xs font-light text-stone-400">
              <span>Phone: <span className="text-stone-200">{e.phone || "—"}</span></span>
              <span>Guests: <span className="text-stone-200">{e.guest_count ?? "—"}</span></span>
              <span>Received: <span className="text-stone-200">{fmtDate(e.created_at)}</span></span>
            </div>
            <div className="mt-6">
              <p className="text-[9px] font-mono tracking-[0.2em] uppercase text-stone-500 flex items-center gap-2">
                <StickyNote size={11} /> Private Notes
              </p>
              <textarea
                data-testid={`enquiry-notes-${e.id}`}
                rows={3}
                value={notes}
                onChange={(ev) => setNotes(ev.target.value)}
                placeholder="Call details, preferences, follow-up reminders…"
                className="mt-2 w-full bg-surface border border-white/10 focus:border-champagne outline-none p-3 text-sm font-light text-ivory placeholder:text-stone-600 resize-none transition-colors"
              />
              <div className="mt-2 flex items-center gap-3">
                <button
                  data-testid={`enquiry-notes-save-${e.id}`}
                  disabled={!notesDirty}
                  onClick={() => onNotes(e.id, notes)}
                  className="border border-champagne/40 px-4 py-2 text-[9px] font-mono tracking-[0.2em] uppercase text-champagne hover:bg-champagne hover:text-obsidian transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  Save Notes
                </button>
                {notesDirty && <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-stone-500">Unsaved changes</span>}
              </div>
            </div>
          </div>
          <div>
            <p className="text-[9px] font-mono tracking-[0.2em] uppercase text-stone-500">Update Status</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  data-testid={`enquiry-set-${s}-${e.id}`}
                  disabled={s === e.status}
                  onClick={() => onStatus(e.id, s)}
                  className="border border-white/15 px-3 py-2 text-[9px] font-mono tracking-[0.2em] uppercase text-stone-300 hover:border-champagne hover:text-champagne transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-3">
              <a data-testid={`enquiry-reply-${e.id}`} href={`mailto:${e.email}?subject=Your wedding commission enquiry`} className="border border-champagne/40 px-4 py-2 text-[9px] font-mono tracking-[0.2em] uppercase text-champagne hover:bg-champagne hover:text-obsidian transition-colors">
                Reply by Email
              </a>
              {confirming ? (
                <button data-testid={`enquiry-delete-confirm-${e.id}`} onClick={() => onDelete(e.id)} className="border border-terracotta px-4 py-2 text-[9px] font-mono tracking-[0.2em] uppercase text-terracotta hover:bg-terracotta hover:text-obsidian transition-colors">
                  Confirm Delete
                </button>
              ) : (
                <button data-testid={`enquiry-delete-${e.id}`} onClick={() => setConfirming(true)} className="p-2 text-stone-500 hover:text-terracotta transition-colors" aria-label="Delete enquiry">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </article>
  );
}
